"use client";

import { useEffect, useRef, useState } from "react";
import { clampSplitterWidth } from "./use-resizable-splitter";

/**
 * Canonical hook for the operator-page detail pane (right-aligned,
 * resizable, persisted, mobile-collapsible).
 *
 * Wraps the existing low-level pieces from `use-resizable-splitter`
 * — `clampSplitterWidth` for the math — but exposes the API the
 * `<OperatorPage.SplitView>` shell needs in one place:
 *
 *   const pane = useResizablePane({ storageKey, defaultWidth: 440 });
 *   pane.enabled        // toggle state, hydration-safe, mobile-aware
 *   pane.toggle()       // user clicked the split-view button
 *   pane.width          // committed width (px) — drives the aside style
 *   pane.commit(next)   // mouseup handler from <ResizeHandle>
 *   pane.min / pane.max // operator's clamp range
 *
 * The right-pane drag uses `window.innerWidth - clientX` so the
 * handle behaves "drag to grow toward the page" — the same gesture
 * the legacy Issues split-view shipped, just centralized here so
 * every operator surface that opts into a split detail pane gets
 * the same width persistence, the same clamp range, the same
 * mobile-breakpoint collapse, and the same hydration-safe SSR
 * behaviour.
 *
 * Pages that need a 3-column grid splitter with internal-scroll
 * panels (Inbox queue/composer/timeline) still use the bespoke
 * `useResizableSplitter` — those layouts have different drag
 * semantics. Both hooks share `clampSplitterWidth` so the clamp
 * vocabulary is the same.
 */

/** Mobile breakpoint — below this we collapse split-view back to
 *  full-page navigation. Single source of truth so every operator
 *  surface treats "no split-view on phones" the same way. */
export const PANE_MOBILE_BREAKPOINT = 768;

/** Canonical pane sizing. Pages can override per-instance, but the
 *  defaults match what every operator surface should ship with. */
export const PANE_DEFAULTS = {
  defaultWidth: 440,
  min: 320,
  max: 720,
} as const;

export type UseResizablePaneOptions = {
  /** localStorage key prefix — the hook stores `{key}:enabled` and
   *  `{key}:width` separately so width persists even when the pane
   *  is collapsed. */
  storageKey: string;
  defaultWidth?: number;
  min?: number;
  max?: number;
  /** Optional viewport breakpoint for mobile collapse. Defaults to
   *  `PANE_MOBILE_BREAKPOINT` (768px). */
  mobileBreakpoint?: number;
  /**
   * Initial enabled state when the operator has no stored preference
   * (first visit, cleared localStorage). Defaults to `false` —
   * matches the historical Issues split-view default. Set `true` for
   * surfaces where the detail pane IS the working area: Inbox needs
   * the message + composer + timeline visible to function at all,
   * so a default-off pane would leave the operator with a queue but
   * no way to read or reply.
   *
   * Once the operator toggles, the stored preference wins regardless
   * of this default.
   */
  defaultEnabled?: boolean;
};

export type UseResizablePaneReturn = {
  /** True only when the operator opted in AND the viewport is wide
   *  enough. Hydration-safe: false on SSR / first render. */
  enabled: boolean;
  hydrated: boolean;
  isMobile: boolean;
  toggle: () => void;

  /** Committed width (drives the aside's inline style). */
  width: number;
  commit: (next: number) => void;
  min: number;
  max: number;
};

export function useResizablePane(opts: UseResizablePaneOptions): UseResizablePaneReturn {
  const min = opts.min ?? PANE_DEFAULTS.min;
  const max = opts.max ?? PANE_DEFAULTS.max;
  const defaultWidth = opts.defaultWidth ?? PANE_DEFAULTS.defaultWidth;
  const breakpoint = opts.mobileBreakpoint ?? PANE_MOBILE_BREAKPOINT;
  const defaultEnabled = opts.defaultEnabled ?? false;
  const enabledKey = `${opts.storageKey}:enabled`;
  const widthKey = `${opts.storageKey}:width`;

  // Toggle state — never render `enabled=true` on first paint so SSR
  // markup matches the client.
  const [hydrated, setHydrated] = useState(false);
  const [enabledPref, setEnabledPref] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [width, setWidth] = useState(defaultWidth);

  useEffect(() => {
    setHydrated(true);
    try {
      // Stored preference wins. When no preference is stored (raw === null)
      // the surface's `defaultEnabled` decides — Inbox needs `true` so the
      // composer surface renders without a manual toggle on first visit.
      const raw = localStorage.getItem(enabledKey);
      setEnabledPref(raw === null ? defaultEnabled : raw === "1");
      const rawWidth = localStorage.getItem(widthKey);
      const parsed = rawWidth ? Number(rawWidth) : NaN;
      if (Number.isFinite(parsed)) {
        setWidth(clampSplitterWidth(parsed, { min, max }));
      }
    } catch { /* localStorage may be disabled */ }

    const mql = typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(`(max-width: ${breakpoint - 1}px)`)
      : null;
    const sync = () => setIsMobile(!!mql?.matches);
    sync();
    mql?.addEventListener?.("change", sync);
    return () => mql?.removeEventListener?.("change", sync);
  }, [enabledKey, widthKey, min, max, breakpoint]);

  const toggle = () => {
    setEnabledPref((v) => {
      const next = !v;
      try { localStorage.setItem(enabledKey, next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  };

  const commit = (next: number) => {
    const clamped = clampSplitterWidth(next, { min, max });
    setWidth(clamped);
    try { localStorage.setItem(widthKey, String(clamped)); } catch { /* noop */ }
  };

  return {
    enabled: hydrated && enabledPref && !isMobile,
    hydrated,
    isMobile,
    toggle,
    width,
    commit,
    min,
    max,
  };
}
